import { Component, HostListener, Input, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
// services
import { AmountFormatterService } from 'src/sale-item/amount-formatter.service';
import { PaymentTermsService } from '../payment-terms.service';
// models
import { PaymentPlanPreview } from '../models/payment-plan-preview.model';
import { Subscription } from 'rxjs';
import { ErrorTriggers } from '../models/error-triggers.model';
import { SeFeThemesLoader } from 'se-fe-themes';
import { SeFeFormFieldConfig } from 'se-fe-form-field';

@Component({
  selector: 'app-upfront-payment-amount',
  templateUrl: './upfront-payment-amount.component.html',
  styleUrls: ['./upfront-payment-amount.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class UpfrontPaymentAmountComponent implements OnInit, OnDestroy {

  public upfrontAmountFieldConfig: SeFeFormFieldConfig = {
    label: 'Upfront payment amount',
    errorMessages: {
      required: 'Enter an upfront payment amount',
      min: 'Enter an upfront payment amount',
      max: 'Enter a valid upfront payment amount',
      pattern: 'Enter a valid upfront payment amount',
    },
  };

  public upfrontAmountInputFocused = false;
  public applied: boolean;
  private triggerErrorSubscription: Subscription;

  @Input() initialAmount: string;
  @Input() previewData: PaymentPlanPreview;
  @Input() paymentTermForm: FormGroup;
  @Input() totalAmount: string;

  constructor(
    private amountFormatter: AmountFormatterService,
    private formBuilder: FormBuilder,
    private paymentTermsService: PaymentTermsService) { 
      SeFeThemesLoader.load({
        supportedThemes: 'light',
        url: 'https://se-fe-angular-services.ui.sportngin.com'
      });
    }

  @HostListener('keydown.enter', ['$event.target.innerText'])
    onKeydownHandler(innerText: string) {
      if (innerText === 'APPLY') {
        this.applyUpfrontAmount();
      }
    }

  ngOnInit(): void {
    this.addUpfrontPaymentControl();
    this.subscribeToTriggerError();
  }

  ngOnDestroy(): void {
    this.triggerErrorSubscription.unsubscribe();
  }

  public get upfrontAmountControl(): AbstractControl {
    return this.paymentTermForm.get('upfrontAmount') as FormControl;
  }

  public setUpfrontAmountFocus(): void {
    this.upfrontAmountInputFocused = true;
  }

  public setStatusAndFormatAmount(): void {
    this.upfrontAmountInputFocused = false;
    this.paymentTermsService.updateErrorTriggers({ appliedStatus: false, emitForm: true });
    this.formatAmount();
  }

  public applyUpfrontAmount(event?: MouseEvent): void {
    this.upfrontAmountControl.markAsDirty();
    if (event) { event.preventDefault(); }
    if (this.upfrontAmountInputFocused) { this.setStatusAndFormatAmount(); }
    if (this.upfrontAmountControl.invalid) {
      this.paymentTermsService.updateErrorTriggers({ appliedStatus: false });
      return;
    }
    this.sendAmountAndSetStatus();
  }

  public resetUpfrontAmount(): void {
    if (this.upfrontAmountControl && this.upfrontAmountControl.invalid) {
      this.upfrontAmountControl.reset();
      this.paymentTermsService.updateErrorTriggers({ appliedStatus: false, emitForm: true });
      this.upfrontAmountControl.setValue(this.initialAmount);
    }
  }

  private sendAmountAndSetStatus(): void {
    if (!this.applied) {
      this.paymentTermsService.upfrontPaymentAmountSubject.next(this.upfrontAmountControl.value);
      this.paymentTermsService.updateErrorTriggers({ appliedStatus: true });
    }
  }

  private subscribeToTriggerError(): void {
    this.triggerErrorSubscription = this.paymentTermsService.$triggerError
      .subscribe((triggers: ErrorTriggers) => {
        this.applied = triggers.appliedStatus;
        if (this.upfrontAmountControl && triggers.triggerError) {
          this.upfrontAmountControl.markAsDirty();
          this.upfrontAmountControl.setErrors({ required: true });
          this.upfrontAmountControl.updateValueAndValidity();
        }
      });
  }

  private formatAmount(): number {
    let amount = this.upfrontAmountControl.value;

    if (!amount) { return; }
    amount = this.sanitizedAmount(amount);
    if (isNaN(amount)) { return; }
    this.upfrontAmountControl.setValue(this.amountFormatter.formattedAmount(amount));
    return amount;
  }

  private addUpfrontPaymentControl(): void {
    const amount = (this.initialAmount && this.initialAmount !== '0.00') ? Number(this.initialAmount) : '';
    this.paymentTermForm.addControl('upfrontAmount', this.formBuilder.control(amount, [
      Validators.pattern(/^(\$)?(([0-9,]+)?([0-9]\.)?(\.[0-9]+)?)$/),
      this.upfrontAmountValidator()]
    ));
    this.paymentTermsService.updateErrorTriggers({ emitForm: true, triggerError: false });
  }

  private upfrontAmountValidator(): ValidatorFn {
    return (formControl: FormControl) => {
      if (!formControl.value) {
        return { required: true };
      } else if (this.sanitizedAmount(formControl.value) <= 0) {
        return { min: true };
      } else if (this.sanitizedAmount(formControl.value) > this.maxUpfrontPayment()) {
        return { max: true };
      }
      return null;
    };
  }

  private sanitizedAmount(amount: string): number {
    return this.amountFormatter.sanitizePrice(amount);
  }

  public maxUpfrontPayment(): number {
    if (!this.previewData) { return; }
    const total = this.sanitizedAmount(this.totalAmount);
    if (Number(this.previewData.initial_payment_amount) > 0) {
      return total - (this.previewData.payments.length - 1) / 100;
    } else {
      return total - (this.previewData.payments.length) / 100;
    }
  }
}
